<template>
  <div class="case-page">
    <section class="page-hero">
      <img src="/img/process-quality-patrol.webp" alt="江苏道鼎升对公工程查验与质量过程管控服务案例" />
      <div class="page-hero-mask"></div>
      <div class="page-hero-content">
        <nav class="breadcrumb" aria-label="面包屑">
          <router-link to="/">首页</router-link>
          <span>/</span>
          <span>案例中心</span>
        </nav>
        <p class="eyebrow">Project References</p>
        <h1>对公工程查验与质量过程管控服务案例</h1>
        <p>
          案例不只展示楼盘名称，更展示项目类型、服务内容、交付成果和项目价值，帮助开发商、建设单位、物业单位和住宅交付项目判断服务适配度。
        </p>
      </div>
    </section>

    <section class="section">
      <div class="section-inner">
        <div class="stats-grid">
          <div v-for="item in stats" :key="item.label" class="stat-card">
            <strong>{{ item.value }}</strong>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="section muted">
      <div class="section-inner">
        <div class="section-heading">
          <p class="eyebrow">Case List</p>
          <h2>典型项目服务记录</h2>
          <p>以下案例为服务场景化呈现，真实项目数据、户数和报告样例可在商务沟通阶段进一步补充确认。</p>
        </div>

        <div class="case-grid">
          <article class="case-card" v-for="item in cases" :key="item.id">
            <img :src="item.image" :alt="item.title + '服务场景示意图'" />
            <div class="case-content">
              <div class="case-meta">
                <span>{{ item.city }}</span>
                <span>{{ item.projectType }}</span>
              </div>
              <h3>{{ item.title }}</h3>
              <p><strong>服务内容：</strong>{{ item.services.join("、") }}</p>
              <p><strong>交付成果：</strong>{{ item.result }}</p>
              <p><strong>项目价值：</strong>{{ item.value }}</p>
              <div class="deliver-tags">
                <span v-for="deliverable in item.deliverables" :key="deliverable">{{ deliverable }}</span>
              </div>
              <router-link class="detail-link" :to="'/cases/' + item.id">查看详情</router-link>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="section method-section">
      <div class="section-inner method-layout">
        <div>
          <p class="eyebrow">How We Work</p>
          <h2>案例交付背后的工作方法</h2>
          <p>道鼎升以第三方身份进入项目现场，把零散问题转化为标准化、可追溯、可整改的交付资料。</p>
        </div>
        <div class="method-grid">
          <div v-for="item in methods" :key="item.title" class="method-card">
            <i :class="item.icon"></i>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section cta-section">
      <div class="section-inner cta-inner">
        <div>
          <h2>想评估您的项目适合哪类查验方案？</h2>
          <p>提供项目类型、城市、预计户数和交付节点，我们会结合服务案例经验给出初步建议。</p>
        </div>
        <router-link class="btn" :to="{ path: '/', hash: '#lead-form' }">提交项目需求</router-link>
      </div>
    </section>

    <div class="beian">
      <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">苏ICP备2024065706号</a>
      <a href="http://www.beian.gov.cn/portal/registerSystemInfo?recordcode=32050602013590" target="_blank" rel="noopener">苏公网安备32050602013590号</a>
    </div>
  </div>
</template>

<script>
import { detailedCases } from "../data/services";
import {
  absoluteUrl,
  breadcrumbSchema,
  localBusinessSchema,
  organizationSchema,
  setSeo,
  setStructuredData
} from "../utils/seo";

export default {
  name: "Case",
  data() {
    return {
      cases: detailedCases,
      stats: [
        { value: "6类", label: "对公服务模块" },
        { value: "长三角", label: "重点服务区域" },
        { value: "B2B", label: "开发商与项目方客户" },
        { value: "闭环", label: "问题清单到整改复查" }
      ],
      methods: [
        {
          icon: "el-icon-s-order",
          title: "标准化问题清单",
          desc: "按户、楼栋、专业类别记录问题，方便工程、客服、施工单位协同整改。"
        },
        {
          icon: "el-icon-camera",
          title: "照片与位置记录",
          desc: "问题描述结合照片和位置，提高整改交底和复查核对效率。"
        },
        {
          icon: "el-icon-data-analysis",
          title: "高频问题汇总",
          desc: "按阶段输出汇总，帮助项目识别共性问题和重点整改方向。"
        },
        {
          icon: "el-icon-finished",
          title: "整改复查闭环",
          desc: "复查已整改、未整改和整改不到位问题，形成可追溯记录。"
        }
      ]
    };
  },
  mounted() {
    setSeo({
      title: "对公工程查验服务案例｜过程质量管控与整改复查｜江苏道鼎升工程检测",
      description:
        "江苏道鼎升工程检测有限公司服务案例展示精装入场查验、过程质量巡检、分户查验、交付前预验收、工程质量检测和整改复查项目的服务内容、交付成果与项目价值。",
      path: "/cases",
      image: "/img/process-quality-patrol.webp"
    });
    setStructuredData([
      organizationSchema(),
      localBusinessSchema(),
      {
        "@type": "CollectionPage",
        name: "对公工程查验与质量过程管控服务案例",
        url: absoluteUrl("/cases"),
        mainEntity: {
          "@type": "ItemList",
          itemListElement: this.cases.map(function(item, index) {
            return {
              "@type": "ListItem",
              position: index + 1,
              name: item.title,
              description: item.result
            };
          })
        }
      },
      breadcrumbSchema([
        { name: "首页", path: "/" },
        { name: "案例中心", path: "/cases" }
      ])
    ]);
  }
};
</script>

<style lang="scss" scoped>
.case-page {
  background: #f5f8f7;
  color: #1f2d3d;
}

.page-hero {
  min-height: 430px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-hero img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.page-hero-mask {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(12, 38, 51, 0.92), rgba(15, 76, 92, 0.68));
}

.page-hero-content,
.section-inner {
  width: min(1180px, calc(100% - 48px));
  margin: 0 auto;
}

.page-hero-content {
  position: relative;
  z-index: 1;
  color: #fff;
}

.breadcrumb {
  display: flex;
  gap: 8px;
  margin-bottom: 26px;
  color: rgba(255, 255, 255, 0.78);
}

.breadcrumb a {
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
}

.eyebrow {
  color: #d76b30;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
}

.page-hero h1 {
  max-width: 860px;
  font-size: 42px;
  line-height: 1.25;
  margin: 0 0 18px;
}

.page-hero p {
  max-width: 860px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 17px;
  line-height: 1.9;
}

.section {
  padding: 72px 0;
}

.muted {
  background: #fff;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.stat-card,
.case-card,
.method-card {
  border: 1px solid #dce6e4;
  border-radius: 8px;
  background: #fff;
}

.stat-card {
  padding: 26px;
}

.stat-card strong {
  display: block;
  color: #0f4c5c;
  font-size: 32px;
  margin-bottom: 8px;
}

.stat-card span {
  color: #5c6873;
}

.section-heading {
  max-width: 780px;
  margin-bottom: 30px;
}

h2 {
  color: #0f4c5c;
  font-size: 31px;
  line-height: 1.35;
  margin: 0 0 14px;
}

.section-heading p,
.method-layout p,
.case-content p,
.cta-section p {
  color: #5c6873;
  line-height: 1.8;
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.case-card {
  overflow: hidden;
}

.case-card img {
  width: 100%;
  height: 260px;
  object-fit: cover;
  display: block;
}

.case-content {
  padding: 26px;
}

.case-meta {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.case-meta span,
.deliver-tags span {
  border-radius: 4px;
  background: #e8f3f1;
  color: #0f4c5c;
  padding: 7px 10px;
  font-size: 13px;
  font-weight: 700;
}

.case-content h3 {
  color: #17313b;
  font-size: 22px;
  line-height: 1.45;
  margin: 0 0 14px;
}

.deliver-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.detail-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  margin-top: 20px;
  padding: 0 16px;
  border-radius: 4px;
  color: #fff;
  background: #18736e;
  text-decoration: none;
  font-weight: 700;
}

.method-section {
  background: #f5f8f7;
}

.method-layout {
  display: grid;
  grid-template-columns: 0.75fr 1.25fr;
  gap: 40px;
}

.method-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.method-card {
  padding: 24px;
}

.method-card i {
  color: #d76b30;
  font-size: 28px;
}

.method-card h3 {
  margin: 16px 0 10px;
  color: #17313b;
}

.cta-section {
  background: #0f4c5c;
}

.cta-inner {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  color: #fff;
}

.cta-inner h2,
.cta-inner p {
  color: #fff;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 22px;
  border-radius: 4px;
  background: #d76b30;
  color: #fff;
  text-decoration: none;
  white-space: nowrap;
}

@media (max-width: 980px) {
  .stats-grid,
  .case-grid,
  .method-layout,
  .method-grid {
    grid-template-columns: 1fr;
  }

  .page-hero h1 {
    font-size: 34px;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .page-hero-content,
  .section-inner {
    width: min(100% - 28px, 1180px);
  }

  .page-hero {
    min-height: auto;
    padding: 70px 0;
  }

  .page-hero h1 {
    font-size: 28px;
  }

  .section {
    padding: 52px 0;
  }
}
</style>
